import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Phone, Menu, X, Mail, MapPin, Clock, Facebook } from "lucide-react";
import { BUSINESS, SERVICE_AREAS } from "@/content/siteContent";

const NAV = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "About", path: "/about" },
  { label: "Reviews", path: "/reviews" },
  { label: "Contact", path: "/contact" },
];

export const QuoteButton = ({ className = "", testId = "header-quote-cta", children }) => (
  <Link
    to="/quote"
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 bg-gold px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.2em] text-navy-950 transition-colors duration-300 hover:bg-gold-light ${className}`}
  >
    {children || "Request a Quote"}
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header data-testid="header-navbar" className="fixed inset-x-0 top-0 z-50">
      <div className="border-b border-white/10 bg-navy-950/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 sm:px-6">
          <p data-testid="veteran-badge" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">
            <Star className="h-3 w-3" fill="currentColor" /> US Military Veteran Owned &amp; Operated
          </p>
          <a href={BUSINESS.phoneHref} data-testid="header-phone-link" className="hidden items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-300 transition-colors hover:text-gold sm:flex">
            <Phone className="h-3 w-3" /> {BUSINESS.phone}
          </a>
        </div>
      </div>
      <div className="border-b border-white/10 bg-navy-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3" aria-label="Home Team Moving Co. home">
            <img src="/logo.png" alt="" className="h-11 w-11 rounded-full" />
            <span className="sr-only">Home Team Moving Co.</span>
            <span className="leading-none" aria-hidden="true">
              <span className="block font-display text-xl font-bold uppercase tracking-wide text-white">Home Team</span>
              <span className="block font-display text-[10px] font-semibold uppercase tracking-[0.35em] text-gold">Moving Co.</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-sm font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <QuoteButton />
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 text-white lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden border-t border-white/10 lg:hidden"
              aria-label="Mobile"
            >
              <div className="flex flex-col gap-1 px-4 py-4">
                {NAV.map((item) => (
                  <button
                    key={item.path}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => {
                      setOpen(false);
                      navigate(item.path);
                    }}
                    className="py-3 text-left font-display text-lg font-semibold uppercase tracking-[0.2em] text-slate-200 transition-colors hover:text-gold"
                  >
                    {item.label}
                  </button>
                ))}
                <div className="pt-2" onClick={() => setOpen(false)}>
                  <QuoteButton className="w-full" testId="mobile-quote-cta" />
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer data-testid="footer-section" className="border-t border-white/10 bg-navy-950">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <div className="mb-4 flex items-center gap-3">
          <img src="/logo.png" alt="" className="h-12 w-12 rounded-full" />
          <span className="leading-none" aria-hidden="true">
            <span className="block font-display text-xl font-bold uppercase tracking-wide text-white">Home Team</span>
            <span className="block font-display text-[10px] font-semibold uppercase tracking-[0.35em] text-gold">Moving Co.</span>
          </span>
        </div>
        <p className="text-sm leading-relaxed text-slate-400">
          Veteran-owned moving &amp; hauling serving {BUSINESS.region}. Discipline, honor, and precision on every job.
        </p>
        <p className="mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
          <Star className="h-3 w-3" fill="currentColor" /> Veteran Owned &amp; Operated
        </p>
      </div>
      <nav aria-label="Services">
        <h3 className="eyebrow mb-5">Services</h3>
        <ul className="space-y-3 text-sm text-slate-400">
          <li><Link className="transition-colors hover:text-gold" to="/services">Residential Moving</Link></li>
          <li><Link className="transition-colors hover:text-gold" to="/services">Junk Removal &amp; Cleanouts</Link></li>
          <li><Link className="transition-colors hover:text-gold" to="/services">Packing &amp; Unpacking</Link></li>
          <li><Link className="transition-colors hover:text-gold" to="/services">Property Turnover</Link></li>
          <li><Link className="font-semibold text-gold transition-colors hover:text-gold-light" to="/services">View all 13 services</Link></li>
        </ul>
      </nav>
      <nav aria-label="Company">
        <h3 className="eyebrow mb-5">Company</h3>
        <ul className="space-y-3 text-sm text-slate-400">
          <li><Link className="transition-colors hover:text-gold" to="/about">About Us</Link></li>
          <li><Link className="transition-colors hover:text-gold" to="/reviews">Reviews</Link></li>
          <li><Link className="transition-colors hover:text-gold" to="/quote">Request a Quote</Link></li>
          <li><Link className="transition-colors hover:text-gold" to="/contact">Contact</Link></li>
        </ul>
      </nav>
      <div>
        <h3 className="eyebrow mb-5">Contact</h3>
        <ul className="space-y-3 text-sm text-slate-400">
          <li><a href={BUSINESS.phoneHref} className="flex items-center gap-2 transition-colors hover:text-gold"><Phone className="h-4 w-4 text-gold" /> {BUSINESS.phone}</a></li>
          <li><a href={BUSINESS.emailHref} className="flex items-center gap-2 break-all transition-colors hover:text-gold"><Mail className="h-4 w-4 shrink-0 text-gold" /> {BUSINESS.email}</a></li>
          <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {BUSINESS.hours}</li>
          <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Serving {SERVICE_AREAS.join(", ")}</li>
          <li><a href={BUSINESS.facebookUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 transition-colors hover:text-gold"><Facebook className="h-4 w-4 text-gold" /> Find us on Facebook</a></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-xs text-slate-500 sm:flex-row sm:px-6">
        <p>© {new Date().getFullYear()} {BUSINESS.name}. {BUSINESS.licenseLine}.</p>
        <p className="flex gap-6">
          <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-gold">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-gold">Terms of Service</Link>
        </p>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-[104px]">{children}</main>
      <Footer />
    </div>
  );
}
